import { useMutation } from "@tanstack/react-query";
import MainContainer from "components/MainContainer";
import ProfileAvatar from "components/ProfileAvatar";
import Spacer from "components/Spacer";
import WhiteText from "components/WhiteText";
import { Image } from "expo-image";
import { useAccount } from "hooks/useAccount";
import useCurrentUser from "hooks/useCurrentUser";
import { MemberCardScreenProps } from "navigation/types";
import i18n from "plugins/localization";
import { getMemberBarcodeFetcher } from "queries/account.queries";
import { useFetchTenantBrandingQuery } from "queries/tenant.queries";
import React, { useEffect, useState } from "react";
import { ImageBackground, StyleSheet, View } from "react-native";
import { svgURL } from "utils/fileUtils";
import { filterIneligibleSubscriptions } from "utils/subscriptionUtils";
import { getUserName, memberId } from "utils/userUtils";
import TenantPromotionComponent from "./TenantPromotionComponent";

const PLAN_BACKGROUND_COLORS: {
  [key: number]: string;
} = {
  132: "#5b2988", // Reign On Demand - Line 3
  133: "#5b2988", // Reign On Demand - Line 2
  134: "#5b2988", // Reign On Demand - Line 1
  652: "#f4d362",
  653: "#f4d362",
  654: "#f4d362",
  655: "#f4d362",
  657: "#f4d362",
};

const MemberCard = ({ navigation }: MemberCardScreenProps) => {
  const { data: branding } = useFetchTenantBrandingQuery();
  const { account } = useAccount();
  const subscriptions = account?.subscriptions || [];
  const [memberBarcode, setMemberBarcode] = useState("");
  const { mutateAsync: fetchMemberBarcode } = useMutation(
    getMemberBarcodeFetcher
  );
  if (!branding || !subscriptions) return null;

  const { currentUser, userImageURL, currentMember } = useCurrentUser();
  const { name, primaryColor } = branding;
  const tenantLogoUrl = svgURL(name);

  // see if PLAN_BACKGROUND_COLORS has any of the plan in current user's subscriptions
  const getPlanBackgroundColor = () => {
    for (const sub of subscriptions) {
      if (
        filterIneligibleSubscriptions(sub) &&
        sub.member &&
        sub.member.id === currentMember?.id &&
        PLAN_BACKGROUND_COLORS[sub.planPackage.id]
      ) {
        return PLAN_BACKGROUND_COLORS[sub.planPackage.id];
      }
    }
    return undefined;
  };

  useEffect(() => {
    async function getMemberBarcode() {
      if (!currentMember) return;
      const response = (await fetchMemberBarcode(currentMember.id)) as any;
      setMemberBarcode(`data:image/png;base64,${response.barcodeBase64Png}`);
    }
    getMemberBarcode();
  }, [currentMember]);

  if (!currentUser || !currentMember) return null;

  return (
    <MainContainer
      title={i18n.t("screens.MemberCard.memberCard")}
      goBackAction={() => navigation.navigate("AccountScreen")}
    >
      <View
        style={{
          flex: 1,
          padding: 15,
        }}
      >
        <View>
          <ImageBackground
            source={{
              uri: tenantLogoUrl,
            }}
            resizeMode="cover"
            imageStyle={styles.logoImageStyle}
            style={[
              styles.logoImageBackground,
              {
                backgroundColor: getPlanBackgroundColor() || primaryColor,
              },
            ]}
          >
            <ImageBackground
              source={require("assets/images/member_card_overlay.gif")}
              style={styles.gifImageBackground}
            >
              <View style={{ padding: 15, flex: 1 }}>
                <View style={{ flexDirection: "row" }}>
                  <ProfileAvatar
                    hasImageIdentifier={currentUser.imageIdentifier !== null}
                    source={{ uri: userImageURL }}
                    size={90}
                  />
                  <WhiteText
                    style={{
                      marginTop: 20,
                      marginLeft: 20,
                    }}
                    category="h5"
                  >
                    {getUserName(currentUser)}
                  </WhiteText>
                </View>

                <Spacer />

                <View>
                  <WhiteText category="h5" selectable>
                    {i18n.t("common.memberId", {
                      memberId: memberId(currentMember?.tenantMemberNumber),
                    })}
                  </WhiteText>
                </View>

                <View style={{ flexGrow: 1 }}>
                  <TenantPromotionComponent />
                </View>

                <View
                  style={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "flex-end",
                  }}
                >
                  <Image
                    source={{ uri: tenantLogoUrl }}
                    style={{
                      width: 50,
                      height: 50,
                    }}
                    contentFit="contain"
                  />
                  <View style={{ height: 120, width: 120 }}>
                    {memberBarcode !== "" && (
                      <Image
                        source={{ uri: memberBarcode }}
                        style={styles.barcodeStyle}
                      ></Image>
                    )}
                  </View>
                </View>
              </View>
            </ImageBackground>
          </ImageBackground>
        </View>
      </View>
    </MainContainer>
  );
};

const IMAGE_BACKGROUND_HEIGHT = 500;
const IMAGE_BACKGROUND_WIDTH = "100%";
const styles = StyleSheet.create({
  rowContainer: {
    flexDirection: "row",
    marginBottom: 10,
  },
  logoImageStyle: {
    transform: [{ scale: 1.2 }],
    opacity: 0.1,
  },
  logoImageBackground: {
    borderRadius: 10,
    borderWidth: 1,
    width: IMAGE_BACKGROUND_WIDTH,
    height: IMAGE_BACKGROUND_HEIGHT,
    overflow: "hidden",
  },
  gifImageBackground: {
    width: IMAGE_BACKGROUND_WIDTH,
    height: IMAGE_BACKGROUND_HEIGHT,
  },
  barcodeStyle: { height: 120, width: 120 },
});
export default MemberCard;
